import { discoverMenuSkeleton, useIntl, weenatIntl as wintl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import { keysOf } from '../../utils';
export const sensorUsages = ['irrigation', 'frost', 'weather'];
export function isSensorArticle(article) {
    return 'metrics' in article;
}
export const DISCOVER_MENU_SECTIONS = keysOf(discoverMenuSkeleton.discover_menu.sections);
const frenchOnlyArticleSlugs = ['irrelis', 'decitrait'];
export function getSectionArticlesSlugs(section, locale) {
    return Object.keys(discoverMenuSkeleton.discover_menu.sections[section].articles).filter((slug) => {
        if (locale !== 'fr') {
            return !frenchOnlyArticleSlugs.includes(slug);
        }
        else {
            return true;
        }
    });
}
const MetricsPerSensors = {
    CHP: ['HPOT', 'T_SOIL'],
    CAPA: ['U_CAPA', 'T_SOIL'],
    LWS: ['LW_D'],
    anemometer: ['DD', 'FF', 'FXY'],
    T_GEL: ['T_WET', 'T_DRY'],
    weather_station: ['T', 'U', 'RR'],
    TH: ['T', 'U'],
    Tmin: ['T_SOIL']
};
const CompatiblePlansPerSlugs = {
    /** SENSORS */
    CHP: ['expert'],
    CAPA: ['expert'],
    LWS: ['expert', 'plus'],
    anemometer: ['expert', 'plus'],
    T_GEL: ['expert', 'plus'],
    weather_station: ['expert', 'plus'],
    TH: ['expert', 'plus'],
    Tmin: ['expert', 'plus'],
    /** APP */
    plots: ['essential', 'expert', 'plus'],
    sensors_map: ['expert', 'plus'],
    history: ['expert', 'plus'],
    forecasts: ['essential', 'expert', 'plus'],
    alerts: ['expert', 'plus'],
    /** DSS */
    weephyt: ['essential', 'expert', 'plus'],
    apple_scab: ['expert', 'plus'],
    decitrait: ['expert', 'plus'],
    irrelis: ['expert', 'plus']
};
const ArticleCategoriesPerSlug = {
    /** Irrigation sensors in order */
    CHP: ['irrigation'],
    CAPA: ['irrigation'],
    /** Frost sensors pin order */
    T_GEL: ['frost'],
    /** Weather sensors pin order */
    weather_station: ['weather'],
    anemometer: ['weather'],
    LWS: ['weather'],
    TH: ['weather'],
    Tmin: ['weather'],
    /** APP */
    plots: [],
    sensors_map: [],
    history: [],
    forecasts: [],
    alerts: [],
    /** DSS */
    weephyt: [],
    apple_scab: [],
    decitrait: [],
    irrelis: []
};
export const getCategoryNameTranslation = (category) => {
    switch (category) {
        case 'frost':
            return wintl.t('map.universes.all.frost', { capitalize: true });
        case 'irrigation':
            return wintl.t('map.universes.all.irrigation', { capitalize: true });
        case 'weather':
            return wintl.t('map.universes.all.weather', { capitalize: true });
        default:
            throw new Error(`The article category ${category} has no translation`);
    }
};
export const getSlugsByCategories = (category) => {
    return Object.entries(ArticleCategoriesPerSlug).reduce((acc, [key, value]) => {
        const slug = key;
        if (value.includes(category)) {
            acc.push(slug);
        }
        return acc;
    }, []);
};
const MAX_NUMBER_OF_LINK_PER_ARTICLE = 5;
export function useBuildArticleFromWintl({ section, slug }) {
    const { t } = useIntl();
    const basePath = `discover_menu.sections.${section}.articles.${slug}`;
    const contentPath = `${basePath}.content`;
    const linksPath = `${basePath}.links`;
    const categories = ArticleCategoriesPerSlug[slug];
    const compatiblePlans = CompatiblePlansPerSlugs[slug];
    const optionalCta = !isEmpty(t(`${contentPath}.optional_cta.link`))
        ? {
            link: t(`${contentPath}.optional_cta.link`),
            // @ts-expect-error action_label can be undefined
            action_label: !isEmpty(t(`${contentPath}.optional_cta.action_label`))
                ? // @ts-expect-error action_label can be undefined
                    t(`${contentPath}.optional_cta.action_label`)
                : section === 'application'
                    ? t('discover_menu.calls_to_action.discover_subscriptions', { capitalize: true })
                    : t('actions.learn_more', { capitalize: true })
        }
        : undefined;
    const optionalImg = 
    // @ts-expect-error url being optional it will sometimes return an error here
    !isEmpty(t(`${contentPath}.optional_img.url`))
        ? {
            // @ts-expect-error alt being optional it will sometimes return an error here
            alt: !isEmpty(t(`${contentPath}.optional_img.alt`))
                ? // @ts-expect-error alt being optional it will sometimes return an error here
                    t(`${contentPath}.optional_img.alt`)
                : undefined,
            // @ts-expect-error url being optional it will sometimes return an error here
            url: t(`${contentPath}.optional_img.url`)
        }
        : undefined;
    const articleBase = {
        id: slug,
        title: t(`${basePath}.title`),
        cover_url: t(`${basePath}.cover_url`),
        overview: t(`${basePath}.overview`),
        content: {
            first_part: {
                content: t(`${contentPath}.first_part.content`),
                // @ts-expect-error title being optional it will sometimes return an error here
                title: !isEmpty(t(`${contentPath}.first_part.title`))
                    ? // @ts-expect-error title being optional it will sometimes return an error here
                        t(`${contentPath}.first_part.title`)
                    : undefined
            },
            main_cta: {
                link: t(`${contentPath}.main_cta.link`),
                action_label: 
                // @ts-expect-error action_label can be undefined
                !isEmpty(t(`${contentPath}.main_cta.action_label`))
                    ? // @ts-expect-error action_label can be undefined
                        t(`${contentPath}.main_cta.action_label`)
                    : section === 'sensors'
                        ? t('discover_menu.main_sensor_article_cta', { capitalize: true })
                        : t('actions.learn_more')
            },
            second_part: {
                content: t(`${contentPath}.second_part.content`),
                title: !isEmpty(t(`${contentPath}.second_part.title`))
                    ? t(`${contentPath}.second_part.title`)
                    : undefined
            },
            optional_cta: optionalCta,
            optional_img: optionalImg
        },
        links: Array.from({ length: MAX_NUMBER_OF_LINK_PER_ARTICLE }, (_, num) => {
            return {
                label: t(`${linksPath}.${num.toString()}.label`),
                url: t(`${linksPath}.${num.toString()}.url`)
            };
        }).filter((link) => !isEmpty(link.url)),
        categories,
        compatiblePlans
    };
    return section === 'sensors'
        ? {
            ...articleBase,
            metrics: MetricsPerSensors[`${slug}`]
        }
        : articleBase;
}
